import { createApi } from "@reduxjs/toolkit/query/react"

import { ApiTypes, UITypes } from "types"

import { baseQueryWithAuth } from "utils/api"
import { prepareQueryParams } from "utils/prepareQueryParams"

export const vendorApi = createApi({
  reducerPath: "vendor",
  tagTypes: ["vendor", "vendor-fulfilled-orders", "vendor-orders"],
  baseQuery: baseQueryWithAuth,
  endpoints: builder => ({
    getWarehouseWidgetInfo: builder.query<ApiTypes.Res.VendorWarehouseWidgetInfo, UITypes.Params>({
      query: arg => {
        return {
          url: `/vendor/widgets/warehouses`,
          method: "GET",
          params: prepareQueryParams(arg),
          headers: { Accept: "application/json" },
        }
      },
    }),
    getOrderStatusWidget: builder.query<ApiTypes.Res.VendorOrderStatus, void>({
      query: () => {
        return {
          url: `/vendor/widgets/orders`,
          method: "GET",
          headers: { Accept: "application/json" },
        }
      },
    }),
    getContractorsWidget: builder.query<ApiTypes.Res.VendorContractorsWidget, void>({
      query: () => {
        return {
          url: `/vendor/widgets/contractors`,
          method: "GET",
          headers: { Accept: "application/json" },
        }
      },
    }),
    getContractorInstalledDevicesByDate: builder.query<
      ApiTypes.Res.InstalledDevicesHistoricalData,
      {
        contractor_id: number
        start_date: string
        end_date: string
      }
    >({
      query: ({ contractor_id, start_date, end_date }) => ({
        url: `/vendor/install-histogram`,
        headers: { Accept: "application/json" },
        method: "GET",
        params: prepareQueryParams({ contractor_id, start_date, end_date }),
      }),
    }),
    getContractorWarrantyDevicesByDate: builder.query<
      ApiTypes.Res.WarrantyDevicesHistoricalData,
      {
        contractor_id: number
        start_date: string
        end_date: string
      }
    >({
      query: ({ contractor_id, start_date, end_date }) => ({
        url: `/vendor/warranty-histogram`,
        headers: { Accept: "application/json" },
        method: "GET",
        params: prepareQueryParams({ contractor_id, start_date, end_date }),
      }),
    }),
    // New Orders API section
    getVendorOrdersNew: builder.query<ApiTypes.Res.VSManagerOrdersNew, UITypes.Params>({
      query: arg => {
        return {
          url: `/vendor/orders2`,
          method: "GET",
          params: prepareQueryParams(arg),
          headers: { Accept: "application/json" },
        }
      },
      providesTags: ["vendor-orders"],
    }),
    getVendorFulfilledOrdersNew: builder.query<ApiTypes.Res.VSManagerOrdersNew, UITypes.Params>({
      query: arg => {
        return {
          url: `/vendor/orders2/fulfilled`,
          method: "GET",
          params: prepareQueryParams(arg),
          headers: { Accept: "application/json" },
        }
      },
      providesTags: ["vendor-fulfilled-orders"],
    }),
    getOrderStatusWidgetNew: builder.query<ApiTypes.Res.VendorOrderStatus, void>({
      query: () => {
        return {
          url: `/vendor/widgets/orders2`,
          method: "GET",
          headers: { Accept: "application/json" },
        }
      },
    }),
    getVendorOrderDevices: builder.query<{ data: string[] }, number>({
      query: id => ({
        url: `/vendor/orders2/item/${id}/devices`,
        method: "GET",
      }),
    }),
    deleteVendorOrderNew: builder.mutation<ApiTypes.Model.OrderNew, number>({
      query: id => ({
        url: `/vendor/orders2/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["vendor-orders"],
    }),
    denyVendorOrder: builder.mutation<ApiTypes.Model.OrderNew, number>({
      query: id => ({
        url: `/vendor/orders2/${id}/deny`,
        method: "POST",
      }),
      invalidatesTags: ["vendor-orders"],
    }),
    fulfillVendorOrder: builder.mutation<ApiTypes.Model.OrderNew, number>({
      query: id => ({
        url: `/vendor/orders2/item/${id}/fulfill`,
        method: "POST",
      }),
      invalidatesTags: ["vendor-orders"],
    }),
    createVendorOrderNew: builder.mutation<ApiTypes.Model.OrderNew, ApiTypes.Req.CreateVendorOrderNew>({
      query: body => ({
        url: `/vendor/orders2`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["vendor-orders"],
    }),
    editVendorOrderNew: builder.mutation<
      ApiTypes.Model.OrderNew,
      { data: ApiTypes.Req.EditVendorOrderNew; id: number }
    >({
      query: body => ({
        url: `/vendor/orders2/${body.id}`,
        method: "POST",
        body: body.data,
      }),
      invalidatesTags: ["vendor-orders"],
    }),
    getVendorOrder: builder.query<{ data: ApiTypes.Model.OrderNew }, number>({
      query: id => ({
        url: `/vendor/orders2/${id}`,
        method: "GET",
      }),
    }),
  }),
})

export const {
  useLazyGetWarehouseWidgetInfoQuery,
  useLazyGetOrderStatusWidgetQuery,
  useLazyGetContractorsWidgetQuery,
  useLazyGetContractorInstalledDevicesByDateQuery,
  useLazyGetContractorWarrantyDevicesByDateQuery,
  useLazyGetVendorOrdersNewQuery,
  useLazyGetVendorFulfilledOrdersNewQuery,
  useLazyGetOrderStatusWidgetNewQuery,
  useLazyGetVendorOrderDevicesQuery,
  useDeleteVendorOrderNewMutation,
  useDenyVendorOrderMutation,
  useFulfillVendorOrderMutation,
  useCreateVendorOrderNewMutation,
  useLazyGetVendorOrderQuery,
  useEditVendorOrderNewMutation,
} = vendorApi
